"use strict";
/**
 * Tally Service
 *
 * Handles all tally-related data fetching, processing, and timeline building.
 * This service encapsulates the complex tally logic that was previously in the main controller.
 *
 * Responsibilities:
 * - Fetch daily/weekly tally data from API
 * - Fetch details and timeline data
 * - Build timeline visualizations
 * - Handle UTC/local date conversions
 * - Manage tally state and caching
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.tallyService = exports.TallyService = void 0;
var TallyService = /** @class */ (function () {
    function TallyService($http, $q, personService) {
        this.$http = $http;
        this.$q = $q;
        this.personService = personService;
    }
    /**
     * Fetch daily tally data
     */
    TallyService.prototype.fetchDailyTally = function (date, mineOnly, openWithinHours, tallyData) {
        tallyData.loading = true;
        var bounds = this.getLocalDayUtcBounds(date);
        this.persistTallySettings(tallyData, date, mineOnly, openWithinHours);
        if (!this.personService.getDailyTally) {
            tallyData.loading = false;
            return this.$q.reject('Person service not available');
        }
        return this.personService
            .getDailyTally({
            date: date,
            startUTC: bounds.startUTC,
            endUTC: bounds.endUTC,
            mineOnly: mineOnly ? 1 : 0,
            openWithinHours: openWithinHours,
        })
            .then(function (resp) {
            if (resp && resp.data && resp.data.Result === 'OK') {
                tallyData.range = resp.data.range;
                tallyData.filters = resp.data.filters;
                tallyData.totals = resp.data.totals;
                tallyData.rows = resp.data.rows || [];
                tallyData.details = resp.data.details || null;
            }
            return resp;
        })
            .finally(function () {
            tallyData.loading = false;
        });
    };
    /**
     * Fetch daily details for timeline and details table
     */
    TallyService.prototype.fetchDailyDetails = function (date, mineOnly, openWithinHours, tallyData, showTimeline) {
        var _this = this;
        tallyData.detailsLoading = true;
        var bounds = this.getLocalDayUtcBounds(date);
        this.logUtcBoundsDebug('fetchDailyDetails', date, bounds);
        if (!this.personService.getDailyTallyWithDetails) {
            tallyData.detailsLoading = false;
            return this.$q.reject('Person service not available');
        }
        // First fetch: Today's records only (for details table)
        return this.personService
            .getDailyTallyWithDetails({
            date: date,
            startUTC: bounds.startUTC,
            endUTC: bounds.endUTC,
            mineOnly: mineOnly ? 1 : 0,
            openWithinHours: 24, // Only today's records for table
        })
            .then(function (resp) {
            if (resp && resp.data && resp.data.Result === 'OK') {
                var todaysDetails = _this.filterTodaysDetails(resp.data.details || [], date);
                tallyData.details = todaysDetails;
                tallyData.timeTrackingGroups = resp.data.timeTrackingGroups || [];
            }
            // If timeline is also visible, build extended timeline
            if (showTimeline || !tallyData.timeline) {
                return _this.buildExtendedTimeline(date, bounds, mineOnly, openWithinHours, tallyData);
            }
        })
            .finally(function () {
            tallyData.detailsLoading = false;
        });
    };
    /**
     * Fetch timeline-only data
     */
    TallyService.prototype.fetchTimelineOnly = function (date, mineOnly, openWithinHours, tallyData) {
        var _this = this;
        tallyData.detailsLoading = true;
        var bounds = this.getLocalDayUtcBounds(date);
        this.logUtcBoundsDebug('fetchTimelineOnly', date, bounds);
        if (!this.personService.getDailyTallyWithDetails) {
            tallyData.detailsLoading = false;
            return this.$q.reject('Person service not available');
        }
        // Fetch today's records for details table
        return this.personService
            .getDailyTallyWithDetails({
            date: date,
            startUTC: bounds.startUTC,
            endUTC: bounds.endUTC,
            mineOnly: mineOnly ? 1 : 0,
            openWithinHours: 24,
        })
            .then(function (resp) {
            if (resp && resp.data && resp.data.Result === 'OK') {
                var todaysDetails = _this.filterTodaysDetails(resp.data.details || [], date);
                tallyData.details = todaysDetails;
                tallyData.timeTrackingGroups = resp.data.timeTrackingGroups || [];
            }
            // Build extended timeline for visualization
            return _this.buildExtendedTimeline(date, bounds, mineOnly, openWithinHours, tallyData);
        })
            .finally(function () {
            tallyData.detailsLoading = false;
        });
    };
    /**
     * Fetch weekly tally data
     */
    TallyService.prototype.fetchWeekTally = function (baseDate, mineOnly, openWithinHours, tallyData) {
        var _this = this;
        tallyData.loading = true;
        var sow = this.startOfWeek(baseDate);
        this.persistWeekDate(sow);
        var days = this.buildWeekDays(sow);
        var promises = days.map(function (day) {
            var _a, _b;
            return ((_b = (_a = _this.personService).getDailyTally) === null || _b === void 0 ? void 0 : _b.call(_a, {
                date: day.date,
                startUTC: _this.getLocalDayUtcBounds(day.date).startUTC,
                endUTC: _this.getLocalDayUtcBounds(day.date).endUTC,
                mineOnly: mineOnly ? 1 : 0,
                openWithinHours: openWithinHours,
            })) || _this.$q.reject('No getDailyTally');
        });
        return this.$q.all(promises)
            .then(function (responses) {
            var outDays = responses.map(function (resp, idx) {
                var ok = resp && resp.data && resp.data.Result === 'OK';
                return ok
                    ? {
                        date: days[idx].date,
                        totals: resp.data.totals || _this.getEmptyTotals(),
                        filters: resp.data.filters,
                    }
                    : {
                        date: days[idx].date,
                        totals: _this.getEmptyTotals(),
                    };
            });
            var agg = _this.aggregateWeekTotals(outDays);
            tallyData.weekDays = outDays;
            tallyData.weekTotals = agg;
            tallyData.range = {
                start: days[0].date + ' 00:00:00',
                end: days[6].date + ' 23:59:59',
            };
        })
            .finally(function () {
            tallyData.loading = false;
        });
    };
    /**
     * Build week timeline by aggregating day details
     */
    TallyService.prototype.buildWeekTimeline = function (tallyData) {
        var _this = this;
        if (!tallyData.weekDays || !tallyData.weekDays.length) {
            return this.$q.reject('No week days data');
        }
        var start = tallyData.weekDays[0].date + ' 00:00:00';
        var end = tallyData.weekDays[tallyData.weekDays.length - 1].date + ' 23:59:59';
        // Fetch details for each day sequentially
        var chain = this.$q.resolve();
        var allDetails = [];
        tallyData.weekDays.forEach(function (day) {
            chain = chain.then(function () {
                var _a, _b;
                var bounds = _this.getLocalDayUtcBounds(day.date);
                return ((_b = (_a = _this.personService).getDailyTallyWithDetails) === null || _b === void 0 ? void 0 : _b.call(_a, {
                    date: day.date,
                    startUTC: bounds.startUTC,
                    endUTC: bounds.endUTC,
                    mineOnly: 1,
                    openWithinHours: 24,
                }).then(function (resp) {
                    if (resp && resp.data && resp.data.Result === 'OK' && Array.isArray(resp.data.details)) {
                        allDetails.push.apply(allDetails, (resp.data.details || []));
                    }
                })) || _this.$q.resolve();
            });
        });
        return chain.then(function () {
            _this.buildDayTimelineExtended(allDetails, start, end);
            return tallyData;
        });
    };
    /**
     * Load stale open records for cleanup
     */
    TallyService.prototype.loadStaleOpen = function (olderThanHours, mineOnly) {
        if (!this.personService.getStaleOpen) {
            return this.$q.reject('Stale open service not available');
        }
        return this.personService.getStaleOpen({
            olderThanHours: olderThanHours,
            mineOnly: mineOnly ? 1 : 0
        });
    };
    // Private helper methods
    TallyService.prototype.getLocalDayUtcBounds = function (date) {
        // Implementation of the UTC bounds calculation
        var localStart = new Date(date + 'T00:00:00');
        var localEnd = new Date(date + 'T23:59:59');
        var utcStartMs = localStart.getTime();
        var utcEndMs = localEnd.getTime();
        var startUTC = new Date(utcStartMs).toISOString().slice(0, 19).replace('T', ' ');
        var endUTC = new Date(utcEndMs).toISOString().slice(0, 19).replace('T', ' ');
        return { startUTC: startUTC, endUTC: endUTC };
    };
    TallyService.prototype.logUtcBoundsDebug = function (context, date, bounds) {
        console.log("".concat(context, " - Date: ").concat(date, ", Bounds:"), bounds);
    };
    TallyService.prototype.persistTallySettings = function (tallyData, date, mineOnly, openHrs) {
        try {
            var localStorage_1 = window.localStorage;
            if (!localStorage_1)
                return;
            localStorage_1.setItem('audroam.tally.date', date);
            localStorage_1.setItem('audroam.tally.mineOnly', mineOnly ? '1' : '0');
            localStorage_1.setItem('audroam.tally.openWithinHours', String(openHrs));
        }
        catch (_a) { }
    };
    TallyService.prototype.filterTodaysDetails = function (details, date) {
        var dayStart = new Date(date + 'T00:00:00');
        var dayEnd = new Date(date + 'T23:59:59');
        var dayStartMs = dayStart.getTime();
        var dayEndMs = dayEnd.getTime();
        return details.filter(function (d) {
            var createdAtStr = d.pnCustomTimeUTC || d.StartUTC || d.startUTC;
            if (!createdAtStr)
                return false;
            var createdAt = new Date(createdAtStr.replace(' ', 'T') + 'Z').getTime();
            var hoursBeforeStart = (dayStartMs - createdAt) / (1000 * 60 * 60);
            var completedAtStr = d.pnCompletedUTC || d.CompletedUTC || d.completedUTC;
            var isOpen = !completedAtStr;
            if (!isOpen) {
                var completedAt = new Date(completedAtStr.replace(' ', 'T') + 'Z').getTime();
                var closedWithinDay = completedAt >= dayStartMs && completedAt <= dayEndMs;
                var createdWithin12HoursBefore = hoursBeforeStart <= 12;
                return createdAt >= dayStartMs || (createdWithin12HoursBefore && closedWithinDay);
            }
            else {
                return hoursBeforeStart <= 24;
            }
        });
    };
    TallyService.prototype.buildExtendedTimeline = function (date, bounds, mineOnly, openWithinHours, tallyData) {
        var _this = this;
        var _a, _b;
        var dayMs = 24 * 60 * 60 * 1000;
        var coreStartMs = new Date(bounds.startUTC.replace(' ', 'T') + 'Z').getTime();
        var coreEndMs = new Date(bounds.endUTC.replace(' ', 'T') + 'Z').getTime();
        var extStartUTC = new Date(coreStartMs - dayMs).toISOString().slice(0, 19).replace('T', ' ');
        var extEndUTC = new Date(coreEndMs + dayMs).toISOString().slice(0, 19).replace('T', ' ');
        return ((_b = (_a = this.personService).getDailyTallyWithDetails) === null || _b === void 0 ? void 0 : _b.call(_a, {
            date: date,
            startUTC: extStartUTC,
            endUTC: extEndUTC,
            mineOnly: mineOnly ? 1 : 0,
            openWithinHours: openWithinHours,
        }).then(function (resp2) {
            var allExtendedDetails = resp2 && resp2.data && resp2.data.Result === 'OK'
                ? resp2.data.details || []
                : [];
            _this.buildDayTimelineExtended(allExtendedDetails, bounds.startUTC, bounds.endUTC);
        })) || this.$q.resolve();
    };
    TallyService.prototype.startOfWeek = function (d) {
        var x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        var dow = x.getUTCDay();
        var diff = dow === 0 ? -6 : 1 - dow;
        x.setUTCDate(x.getUTCDate() + diff);
        return new Date(Date.UTC(x.getFullYear(), x.getUTCMonth(), x.getUTCDate()));
    };
    TallyService.prototype.persistWeekDate = function (sow) {
        var _a;
        try {
            (_a = window.localStorage) === null || _a === void 0 ? void 0 : _a.setItem('audroam.tally.date', sow.toISOString().slice(0, 10));
        }
        catch (_b) { }
    };
    TallyService.prototype.buildWeekDays = function (sow) {
        var days = [];
        for (var i = 0; i < 7; i++) {
            var d = new Date(sow.getTime());
            d.setUTCDate(d.getUTCDate() + i);
            days.push({ date: d.toISOString().slice(0, 10) });
        }
        return days;
    };
    TallyService.prototype.getEmptyTotals = function () {
        return {
            durSec: 0,
            billableSec: 0,
            nonBillableSec: 0,
            travelSec: 0,
            sessionSec: 0,
            breakSec: 0,
        };
    };
    TallyService.prototype.aggregateWeekTotals = function (outDays) {
        var agg = __assign({}, this.getEmptyTotals());
        outDays.forEach(function (d) {
            if (d && d.totals) {
                agg.durSec += d.totals.durSec || 0;
                agg.billableSec += d.totals.billableSec || 0;
                agg.nonBillableSec += d.totals.nonBillableSec || 0;
                agg.travelSec += d.totals.travelSec || 0;
                agg.sessionSec += d.totals.sessionSec || 0;
                agg.breakSec += d.totals.breakSec || 0;
            }
        });
        return agg;
    };
    // Timeline building methods would go here
    // These are complex and would need to be extracted from the controller
    TallyService.prototype.buildDayTimelineExtended = function (details, startUTC, endUTC) {
        // Placeholder - this would contain the complex timeline building logic
        // extracted from the controller
    };
    return TallyService;
}());
exports.TallyService = TallyService;
// Factory function for Angular (minification-safe)
exports.tallyService = [
    '$http',
    '$q',
    'personService',
    function ($http, $q, personService) {
        return new TallyService($http, $q, personService);
    }
];
